import { useCallback, useEffect, useMemo, useState } from "react";
import type { IStAdjustmentGroup } from "./types";
import { pushableGroups } from "./utils";

// Which technicians the header action will push. Empty means "everything pending" —
// the server does that without enumerating, so an empty selection is a valid
// state rather than a disabled one.
//
// Unlike PayPacks this does NOT default to selecting everything: "push all" is
// already the empty-selection behaviour, and pre-checking hundreds of rows would
// make the far cheaper key-less bulk request unreachable.
export const useTechnicianSelection = ({
  groups,
}: {
  groups: IStAdjustmentGroup[];
}) => {
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState<
    Set<number>
  >(() => new Set());

  const pushable = useMemo(() => pushableGroups(groups), [groups]);

  // Drop ids that are no longer pushable — a technician whose lines landed
  // mid-push shouldn't keep inflating the count. Returns the same Set when
  // nothing changed, so this effect can't loop on `groups` identity.
  useEffect(() => {
    setSelectedTechnicianIds((current) => {
      if (current.size === 0) return current;

      const stillPushable = new Set(pushable.map((g) => g.employee_id));
      const next = new Set<number>();
      current.forEach((id) => {
        if (stillPushable.has(id)) next.add(id);
      });
      return next.size === current.size ? current : next;
    });
  }, [pushable]);

  const selectedGroups = pushable.filter((group) =>
    selectedTechnicianIds.has(group.employee_id),
  );

  // Stable identities: these are dependencies of the column definitions the
  // adjustments table memoizes. Fresh closures each render would rebuild every
  // column on every push-poll tick, defeating that memo entirely.
  const toggleTechnician = useCallback(
    (group: IStAdjustmentGroup, checked: boolean) => {
      const id = group.employee_id;
      if (!id) return;

      setSelectedTechnicianIds((current) => {
        const next = new Set(current);
        if (checked) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const clear = useCallback(() => setSelectedTechnicianIds(new Set()), []);

  const toggleAll = useCallback(
    (checked: boolean) =>
      setSelectedTechnicianIds(
        checked ? new Set(pushable.map((g) => g.employee_id)) : new Set(),
      ),
    [pushable],
  );

  return {
    selectedTechnicianIds,
    pushableGroups: pushable,
    selectedGroups,
    hasSelection: selectedGroups.length > 0,
    allPushableSelected:
      pushable.length > 0 && selectedGroups.length === pushable.length,
    somePushableSelected: selectedGroups.length > 0,
    selectedLines: selectedGroups.reduce((sum, g) => sum + g.pending, 0),
    selectedCents: selectedGroups.reduce(
      (sum, g) => sum + g.pending_amount_cents,
      0,
    ),
    toggleTechnician,
    toggleAll,
    // Run-change reset. The ids of a previous period's technicians can still be
    // pushable in this one, so the prune above would keep them checked.
    clear,
  };
};
